import { useCallback, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '@/atomic/molecules';
import { SCUIContent } from '@/atomic/organisms';
import { SCUILoadingLinear } from '@/atomic/atoms';
import { PageUrl } from '@/types/enums';
import { CommissionListTab, BulkExcelUploadTab } from './components';
import { CargoQuery } from '@/services/query';
import { MPODC } from './mpPaymentOnDeliveryCommission.classes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
    className?: string;
}

const TabPanel = ({ children, value, index, className, ...other }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return (
        <Paper
            component="div"
            className={className}
            role="tabpanel"
            id={`mp-cod-commission-tabpanel-${index}`}
            aria-labelledby={`mp-cod-commission-tab-${index}`}
            sx={{
                p: '24px',
                display: 'flex',
                flex: 1,
                backgroundColor: 'grey.50',
            }}
            {...other}
        >
            {children}
        </Paper>
    );
};

interface TabConfig {
    label: string;
    Component: React.ComponentType<any>;
    tabClassName: string;
    tabPanelClassName: string;
}

interface CompanyOption {
    id: number;
    name: string;
}

export const MPPaymentOnDeliveryCommission = () => {
    const [tabValue, setTabValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<CompanyOption[]>([]);

    const { t } = useTranslation();

    const handleChange = (_: any, newValue: number): void => {
        setTabValue(newValue);
    };

    const tabsConfig: TabConfig[] = [
        {
            label: t('mpPaymentOnDeliveryCommission.tabs.list'),
            Component: CommissionListTab as React.ComponentType<any>,
            tabClassName: MPODC.tabList,
            tabPanelClassName: MPODC.tabPanelList,
        },
        {
            label: t('mpPaymentOnDeliveryCommission.tabs.bulkUpload'),
            Component: BulkExcelUploadTab as React.ComponentType<any>,
            tabClassName: MPODC.tabBulkUpload,
            tabPanelClassName: MPODC.tabPanelBulkUpload,
        },
    ];

    const getCompanies = useCallback(() => {
        setIsLoading(true);
        CargoQuery.CompanyList()
            .then((res: any) => {
                setCompanyList(res?.data || []);
            })
            .catch(() => setCompanyList([]))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        getCompanies();
    }, []);

    return (
        <SCUIContent>
            <Box className={MPODC.page}>
                <Paper
                    className={MPODC.shellPaper}
                    component="div"
                    sx={{
                        p: '24px',
                        paddingTop: '36px',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <div className={MPODC.breadcrumbSection}>
                        <SCUIBreadCrumb
                            title={t('mpPaymentOnDeliveryCommission.title')}
                            componentLink={PageUrl.mpPaymentOnDeliveryCommission.url}
                        />
                    </div>
                    <div className={MPODC.loadingSection}>
                        <SCUILoadingLinear display={isLoading} />
                    </div>
                    <Box className={MPODC.tabsWrap}>
                        <Tabs
                            className={MPODC.tabsRoot}
                            sx={{ marginTop: '12px' }}
                            value={tabValue}
                            onChange={handleChange}
                            aria-label="MP Payment On Delivery Commission Tabs"
                        >
                            {tabsConfig.map((tab, index) => (
                                <Tab
                                    key={index}
                                    className={tab.tabClassName}
                                    sx={{ fontSize: '14px' }}
                                    label={tab.label}
                                    id={`mp-cod-commission-tab-${index}`}
                                    aria-controls={`mp-cod-commission-tabpanel-${index}`}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    {tabsConfig.map((tab, index) => (
                        <TabPanel key={index} value={tabValue} index={index} className={tab.tabPanelClassName}>
                            <tab.Component setIsLoading={setIsLoading} isLoading={isLoading} companyOptions={companyList} />
                        </TabPanel>
                    ))}
                </Paper>
            </Box>
        </SCUIContent>
    );
};
